import { Badge, Chip } from "@vb/ui";
import { formatCryptoCompact, formatCryptoShort } from "../lib/format.js";
import { useGame } from "../store.js";

interface TopBarProps {
  balance: number;
  currency: string;
  onFairClick: () => void;
}

const ICON_BTN_STYLE: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid var(--line)",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--fg-2)",
  padding: 0,
};

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 5L6 9H2v6h4l5 4V5z" />
      {muted ? (
        <>
          <line x1="23" y1="9" x2="17" y2="15" />
          <line x1="17" y1="9" x2="23" y2="15" />
        </>
      ) : (
        <>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </>
      )}
    </svg>
  );
}

function EyeIcon({ hidden }: { hidden: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {hidden ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

function ShieldIcon({ active }: { active: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      {active && <path d="M9 12l2 2 4-4" />}
    </svg>
  );
}

export function TopBar({ balance, currency, onFairClick }: TopBarProps) {
  const full = formatCryptoShort(balance, currency);
  const compact = formatCryptoCompact(balance, currency);
  const demoMode = useGame((s) => s.demoMode);
  const muted = useGame((s) => s.muted);
  const hideBalance = useGame((s) => s.hideBalance);
  const fairnessOverlay = useGame((s) => s.fairnessOverlay);
  const setMuted = useGame((s) => s.setMuted);
  const setHideBalance = useGame((s) => s.setHideBalance);
  const setFairnessOverlay = useGame((s) => s.setFairnessOverlay);
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 14px",
        height: 44,
        borderBottom: "1px solid var(--line)",
        background: "var(--bg-1)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Operator-neutral chrome — game name only, no studio mark.
           When iframed by an operator, no studio brand surfaces. */}
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 16,
          letterSpacing: "0.06em",
          color: "var(--fg-1)",
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        Vault Breaker
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {demoMode && (
          <Badge
            kind="live"
            style={{
              padding: "3px 8px",
              fontSize: 9,
              letterSpacing: "0.12em",
              background: "rgba(255,165,0,0.15)",
              borderColor: "rgba(255,165,0,0.45)",
              color: "#FFCB7A",
            }}
            title="Backend unreachable — synthetic session for visual review (dev only)"
          >
            DEMO MODE
          </Badge>
        )}
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          aria-label={muted ? "Unmute sound" : "Mute sound"}
          title={muted ? "Unmute" : "Mute"}
          style={ICON_BTN_STYLE}
        >
          <SpeakerIcon muted={muted} />
        </button>
        <button
          type="button"
          onClick={() => setHideBalance(!hideBalance)}
          aria-label={hideBalance ? "Show balance" : "Hide balance"}
          title={hideBalance ? "Show balance" : "Hide balance"}
          style={ICON_BTN_STYLE}
        >
          <EyeIcon hidden={hideBalance} />
        </button>
        <button
          type="button"
          onClick={() => setFairnessOverlay(!fairnessOverlay)}
          aria-label={fairnessOverlay ? "Hide fairness overlay" : "Show fairness overlay"}
          title={fairnessOverlay ? "Hide fairness overlay" : "Show fairness overlay"}
          style={{
            ...ICON_BTN_STYLE,
            color: fairnessOverlay ? "var(--cyan)" : "var(--fg-2)",
            borderColor: fairnessOverlay ? "var(--cyan)" : "var(--line)",
          }}
        >
          <ShieldIcon active={fairnessOverlay} />
        </button>
        <Chip
          live
          dotColor="var(--green)"
          onClick={onFairClick}
          style={{ cursor: "pointer", padding: "4px 10px", fontSize: 12 }}
          title="Provably fair — seed committed and verifiable"
        >
          FAIR
        </Chip>
        <Chip
          style={{ background: "var(--bg-2)", padding: "4px 10px", fontSize: 12 }}
          title={hideBalance ? "Balance hidden" : `${full} ${currency}`}
        >
          <span className="mono" style={{ color: "var(--fg-1)" }}>
            {hideBalance ? "•••••" : compact}
          </span>
          <span
            className="unit"
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "var(--fg-3)",
            }}
          >
            {currency}
          </span>
        </Chip>
      </div>
    </header>
  );
}
